import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, Session } from '@supabase/supabase-js'
import type { UserProfile, UserPreferences } from '@/types'

const isDev = import.meta.env.DEV

// Check if Supabase is configured
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const isSupabaseConfigured = !!(supabaseUrl && supabaseKey && !supabaseUrl.includes('your_'))

// Only import Supabase if configured — loaded lazily in initialize()
let supabase: any = null
let auth: any = null
let supabaseLoaded = false

async function loadSupabase() {
  if (supabaseLoaded || !isSupabaseConfigured) return
  const module = await import('@/services/supabase')
  supabase = module.supabase
  auth = module.auth
  supabaseLoaded = true
}

// Mock user data for demo mode
const MOCK_USER: User = {
  id: 'demo-user-123',
  email: 'demo@printhq.app',
  aud: 'authenticated',
  role: 'authenticated',
  created_at: new Date().toISOString(),
  app_metadata: {},
  user_metadata: { name: 'Demo User' }
} as User

const MOCK_PROFILE: UserProfile = {
  id: 'demo-profile-123',
  user_id: 'demo-user-123',
  email: 'demo@printhq.app',
  display_name: 'Demo User',
  avatar_url: null,
  subscription_tier: 'personal',
  subscription_status: 'active',
  stripe_customer_id: null,
  is_admin: false,
  storage_used_bytes: 0,
  storage_extra_gb: 0,
  preferences: {
    theme: 'dark',
    notifications_enabled: true,
    email_notifications: true,
    currency: 'USD',
    units: 'metric',
    language: 'en',
    ai_include_inventory: true,
  },
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
} as UserProfile

const DEMO_STORAGE_KEY = 'printhq_demo_session'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const session = ref<Session | null>(null)
  const profile = ref<UserProfile | null>(null)
  const loading = ref(true)
  const error = ref<string | null>(null)
  const isDemoMode = ref(!isSupabaseConfigured)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const userId = computed(() => user.value?.id)
  const userEmail = computed(() => user.value?.email)
  const displayName = computed(() => profile.value?.display_name || user.value?.email?.split('@')[0] || 'User')
  const avatarUrl = computed(() => profile.value?.avatar_url)
  const subscriptionTier = computed(() => profile.value?.subscription_tier || 'free')
  const isAdmin = computed(() => profile.value?.is_admin === true)
  const storageUsedBytes = computed(() => profile.value?.storage_used_bytes || 0)
  const storageExtraGb = computed(() => profile.value?.storage_extra_gb || 0)
  const preferences = computed(() => profile.value?.preferences)

  // ============================================
  // DEMO MODE FUNCTIONS
  // ============================================
  
  const initializeDemo = () => {
    // Check if there's a saved demo session
    const savedSession = localStorage.getItem(DEMO_STORAGE_KEY)
    if (savedSession) {
      user.value = MOCK_USER
      profile.value = JSON.parse(savedSession)
    }
    loading.value = false
  }

  const signInDemo = (email: string, _password: string) => {
    // Accept any credentials in demo mode
    user.value = { ...MOCK_USER, email }
    profile.value = {
      ...MOCK_PROFILE,
      email,
      display_name: email.split('@')[0],
      is_admin: email.startsWith('admin')
    }
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(profile.value))
    return { success: true }
  }

  const signOutDemo = async () => {
    user.value = null
    session.value = null
    profile.value = null
    localStorage.removeItem(DEMO_STORAGE_KEY)

    // Clear chat state (module-level singleton)
    try {
      const { useChat } = await import('@/composables/useChat')
      const { resetChatState } = useChat()
      resetChatState()
    } catch { /* chat module may not be loaded */ }

    // Clear all data stores
    try {
      const [
        { usePrinterStore },
        { useFilamentStore },
        { usePrintStore },
        { useProjectStore },
        { useMaintenanceStore },
        { useFileStore },
        { useCustomBrandsStore },
        { useCustomMaterialsStore },
        { useTeamStore },
        { useTeamMembersStore },
      ] = await Promise.all([
        import('./printer'),
        import('./filament'),
        import('./print'),
        import('./project'),
        import('./maintenance'),
        import('./file'),
        import('./customBrands'),
        import('./customMaterials'),
        import('./team'),
        import('./teamMembers'),
      ])
      usePrinterStore().clearData()
      useFilamentStore().clearData()
      usePrintStore().clearData()
      useProjectStore().clearData()
      useMaintenanceStore().clearData()
      useFileStore().clearData()
      useCustomBrandsStore().clearData()
      useCustomMaterialsStore().clearData()
      useTeamStore().clearData()
      useTeamMembersStore().clearData()
    } catch { /* stores may not be loaded */ }

    return { success: true }
  }

  const updateProfileDemo = (updates: Partial<UserProfile>) => {
    if (profile.value) {
      profile.value = { ...profile.value, ...updates }
      localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(profile.value))
    }
    return { success: true }
  }

  // ============================================
  // MAIN ACTIONS (route to demo or Supabase)
  // ============================================

  // ─── Session Freshness Guard ────────────────────────────────
  // Supabase's autoRefreshToken relies on setTimeout which browsers
  // heavily throttle (or stop entirely) when a tab is backgrounded.
  // This means the JWT can expire while the user is away.
  //
  // We solve this in two places:
  //
  // 1. visibilitychange listener — proactively refreshes whenever the
  //    tab is foregrounded after being hidden.
  //
  // 2. ensureFreshSession() — called by the router guard before every
  //    navigation. It reads the JWT's exp claim and refreshes if the
  //    token is expired or about to expire (< 2 min remaining). This
  //    also catches the case where the tab was "visible" but idle
  //    (e.g. user walked away and came back — visibilitychange never
  //    fires in that scenario).
  //
  // Both share a single in-flight promise to avoid duplicate refreshes.

  let visibilityHandler: (() => void) | null = null
  let refreshPromise: Promise<void> | null = null

  /** Race a promise against a timeout. Resolves (never rejects) so callers aren't blocked. */
  const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T | undefined> =>
    Promise.race([
      promise,
      new Promise<undefined>(resolve => setTimeout(resolve, ms)),
    ])

  /** Actually perform the refresh. De-duped so concurrent callers share one request. */
  const doRefresh = (): Promise<void> => {
    if (refreshPromise) return refreshPromise

    refreshPromise = (async () => {
      try {
        // 8-second timeout — if the network is dead after tab sleep,
        // we must not block the router forever.
        const result = await withTimeout(
          supabase.auth.refreshSession(),
          8000
        )

        if (!result) {
          // Timed out — the token may still be usable if not yet expired,
          // so don't sign the user out, just let the navigation proceed.
          console.warn('[auth] Session refresh timed out, proceeding with current token')
          return
        }

        const { data, error: refreshError } = result

        if (refreshError || !data.session) {
          console.warn('[auth] Session expired, signing out')
          user.value = null
          session.value = null
          profile.value = null
        } else {
          session.value = data.session
          user.value = data.session.user
        }
      } catch (err) {
        console.warn('[auth] Session refresh failed:', err)
      } finally {
        refreshPromise = null
      }
    })()

    return refreshPromise
  }

  /**
   * Called by the router guard before every authenticated navigation.
   * Checks the JWT expiry and refreshes if stale. Returns immediately
   * if the token is still fresh. Will never block longer than ~8 s.
   *
   * Returns `true` if a refresh was performed (or was in-flight),
   * so the caller knows to force-reload stores with fresh data.
   */
  const ensureFreshSession = async (): Promise<boolean> => {
    if (isDemoMode.value || !session.value) return false

    // If a refresh is already in flight, wait for it
    if (refreshPromise) {
      await refreshPromise
      return true
    }

    // Decode the JWT expiry (exp is in seconds)
    const token = session.value.access_token
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const expiresAt = payload.exp * 1000 // ms
      const buffer = 2 * 60 * 1000 // refresh if < 2 min remaining
      if (Date.now() < expiresAt - buffer) return false // still fresh
    } catch {
      // If we can't decode the token, refresh to be safe
    }

    await doRefresh()
    return true
  }

  const setupVisibilityRefresh = () => {
    if (isDemoMode.value || visibilityHandler) return

    visibilityHandler = () => {
      if (document.visibilityState !== 'visible') return
      if (!user.value) return
      // Fire-and-forget — the router guard will also call ensureFreshSession
      // before navigating, so this is just an eager head-start.
      doRefresh()
    }

    document.addEventListener('visibilitychange', visibilityHandler)
  }

  const initialize = async () => {
    try {
      loading.value = true

      // Load Supabase module
      await loadSupabase()

      if (isDemoMode.value) {
        console.log('🔧 PrintHQ running in DEMO MODE (Supabase not configured)')
        initializeDemo()
        return
      }

      // Real Supabase initialization
      const { data: sessionData } = await auth.getSession()
      session.value = sessionData.session
      user.value = sessionData.session?.user || null

      if (user.value) {
        await fetchProfile()
        // Sync subscription tier from Stripe → DB, then re-fetch profile
        await syncSubscriptionTier()
      }

      supabase.auth.onAuthStateChange((event: string, newSession: Session | null) => {
        session.value = newSession
        user.value = newSession?.user || null

        if (event === 'SIGNED_IN' && user.value) {
          // IMPORTANT: Do NOT await fetchProfile() here. This callback can
          // run inside Supabase's navigator.locks context. Awaiting a
          // Supabase query (which needs the same lock) causes a deadlock
          // that permanently blocks ALL subsequent Supabase calls.
          // Deferring to a new macrotask avoids the deadlock entirely.
          setTimeout(() => fetchProfile(), 0)
        } else if (event === 'SIGNED_OUT') {
          profile.value = null
        }
      })

      // Start watching for tab visibility changes to refresh stale sessions
      setupVisibilityRefresh()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to initialize auth'
    } finally {
      loading.value = false
    }
  }

  const fetchProfile = async () => {
    if (isDemoMode.value || !user.value) return

    const { data, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', user.value.id)
      .single()

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error fetching profile:', fetchError)
      return
    }

    profile.value = data as UserProfile

    // Load file encryption key if available
    if (data?.encryption_key_wrapped) {
      try {
        const { isEncryptionConfigured, loadUserKey } = await import('@/composables/useFileEncryption')
        if (isEncryptionConfigured()) {
          await loadUserKey(data.encryption_key_wrapped)
        }
      } catch (err) {
        console.warn('Failed to load encryption key:', err)
      }
    }
  }

  const signUp = async (email: string, password: string, displayName?: string) => {
    if (isDemoMode.value) {
      return signInDemo(email, password)
    }

    try {
      loading.value = true
      error.value = null

      const { data, error: signUpError } = await auth.signUp(email, password)
      
      if (signUpError) throw signUpError

      if (data.user) {
        await supabase.from('profiles').insert({
          user_id: data.user.id,
          email,
          display_name: displayName || email.split('@')[0],
          subscription_tier: 'free',
          subscription_status: 'active',
          preferences: getDefaultPreferences()
        })
        if (data.session) {
          const { claimStoredAffiliateLink } = await import('@/services/affiliateAttribution')
          await claimStoredAffiliateLink().catch(err => console.warn('[affiliate] Signup attribution failed:', err))
        }
      }

      return { success: true }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Sign up failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const signIn = async (email: string, password: string) => {
    if (isDemoMode.value) {
      loading.value = true
      await new Promise(resolve => setTimeout(resolve, 500)) // Fake delay
      const result = signInDemo(email, password)
      loading.value = false
      return result
    }

    try {
      loading.value = true
      error.value = null

      const { error: signInError } = await auth.signIn(email, password)

      if (signInError) throw signInError

      // Fetch the session so user.value is set before we return
      const { data: sessionData } = await auth.getSession()
      session.value = sessionData.session
      user.value = sessionData.session?.user || null

      if (user.value) {
        await fetchProfile()
        const { claimStoredAffiliateLink } = await import('@/services/affiliateAttribution')
        await claimStoredAffiliateLink().catch(err => console.warn('[affiliate] Sign-in attribution failed:', err))
      }

      return { success: true }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Sign in failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const signInWithGoogle = async () => {
    if (isDemoMode.value) {
      return signInDemo('google-user@gmail.com', '')
    }

    try {
      loading.value = true
      error.value = null

      const { error: signInError } = await auth.signInWithGoogle()
      
      if (signInError) throw signInError

      return { success: true }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Google sign in failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const signInWithGithub = async () => {
    if (isDemoMode.value) {
      return signInDemo('github-user@github.com', '')
    }

    try {
      loading.value = true
      error.value = null

      const { error: signInError } = await auth.signInWithGithub()
      
      if (signInError) throw signInError

      return { success: true }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'GitHub sign in failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const linkDiscord = async () => {
    if (isDemoMode.value) {
      return { success: false, error: 'Not available in demo mode' }
    }
    try {
      const { error: linkError } = await auth.linkDiscord()
      if (linkError) throw linkError
      return { success: true }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to link Discord' }
    }
  }

  const discordStatus = async () => {
    if (isDemoMode.value) return null
    const { data, error } = await auth.discordStatus()
    return error ? null : data
  }

  const unlinkDiscord = async () => {
    if (isDemoMode.value) {
      return { success: false, error: 'Not available in demo mode' }
    }
    const { error } = await auth.unlinkDiscord()
    return error ? { success: false, error: error.message || 'Failed to unlink Discord' } : { success: true }
  }

  const unlinkIdentity = async (identityId: string) => {
    if (isDemoMode.value) {
      return { success: false, error: 'Not available in demo mode' }
    }
    try {
      const { error: unlinkError } = await auth.unlinkIdentity(identityId)
      if (unlinkError) throw unlinkError
      return { success: true }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to unlink account' }
    }
  }

  const getUserIdentities = async () => {
    if (isDemoMode.value) return []
    try {
      const { data } = await auth.getUserIdentities()
      return data?.identities || []
    } catch {
      return []
    }
  }

  const signOut = async () => {
    if (isDemoMode.value) {
      loading.value = true
      await new Promise(resolve => setTimeout(resolve, 300))
      const result = signOutDemo()
      loading.value = false
      return result
    }

    try {
      loading.value = true
      error.value = null

      // Remove visibility listener
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler)
        visibilityHandler = null
      }

      // Always clear local state first so the user is logged out
      // even if the Supabase API call fails
      user.value = null
      session.value = null
      profile.value = null

      // Clear chat state (module-level singleton)
      try {
        const { useChat } = await import('@/composables/useChat')
        const { resetChatState } = useChat()
        resetChatState()
      } catch { /* chat module may not be loaded */ }

      // Clear file encryption key from memory
      try {
        const { clearUserKey } = await import('@/composables/useFileEncryption')
        clearUserKey()
      } catch { /* encryption module may not be loaded */ }

      // Clear all data stores
      try {
        const [
          { usePrinterStore },
          { useFilamentStore },
          { usePrintStore },
          { useProjectStore },
          { useMaintenanceStore },
          { useFileStore },
          { useCustomBrandsStore },
          { useCustomMaterialsStore },
          { useTeamStore },
          { useTeamMembersStore },
        ] = await Promise.all([
          import('./printer'),
          import('./filament'),
          import('./print'),
          import('./project'),
          import('./maintenance'),
          import('./file'),
          import('./customBrands'),
          import('./customMaterials'),
          import('./team'),
          import('./teamMembers'),
        ])
        usePrinterStore().clearData()
        useFilamentStore().clearData()
        usePrintStore().clearData()
        useProjectStore().clearData()
        useMaintenanceStore().clearData()
        useFileStore().clearData()
        useCustomBrandsStore().clearData()
        useCustomMaterialsStore().clearData()
        useTeamStore().clearData()
        useTeamMembersStore().clearData()
      } catch { /* stores may not be loaded */ }

      // Then notify Supabase (best-effort)
      try {
        await auth.signOut()
      } catch (err) {
        console.error('Supabase sign out error (ignored):', err)
      }

      return { success: true }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Sign out failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (isDemoMode.value) {
      return updateProfileDemo(updates)
    }

    if (!user.value) return { success: false, error: 'Not authenticated' }

    try {
      error.value = null

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('user_id', user.value.id)

      if (updateError) throw updateError

      await fetchProfile()

      return { success: true }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Update failed'
      return { success: false, error: error.value }
    }
  }

  const updatePreferences = async (updates: Partial<UserPreferences>) => {
    if (!profile.value) return { success: false, error: 'No profile' }

    return updateProfile({
      preferences: { ...profile.value.preferences, ...updates }
    })
  }

  const resetPassword = async (email: string) => {
    if (isDemoMode.value) {
      return { success: true } // Just pretend it worked
    }

    try {
      loading.value = true
      error.value = null

      const { error: resetError } = await auth.resetPassword(email)
      
      if (resetError) throw resetError

      return { success: true }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Password reset failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const changePassword = async (newPassword: string) => {
    if (isDemoMode.value) {
      return { success: true }
    }

    try {
      loading.value = true
      error.value = null

      const { error: updateError } = await auth.updatePassword(newPassword)

      if (updateError) throw updateError

      return { success: true }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Password change failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // Sync subscription tier from Stripe via the get-subscription edge function
  // This edge function now also updates the profiles table if tier is out of date
  const syncSubscriptionTier = async () => {
    if (isDemoMode.value || !user.value) return
    try {
      const { data, error: fnError } = await supabase.functions.invoke('get-subscription', {
        headers: isDev ? { 'x-stripe-test-mode': 'true', 'x-app-origin': window.location.origin } : {},
      })
      if (fnError) {
        console.warn('syncSubscriptionTier error:', fnError)
        return
      }
      if (!data) return
      console.log('Stripe subscription sync:', { stripeTier: data.tier, dbTier: profile.value?.subscription_tier })
      // If the tier from Stripe differs, update local profile directly
      // The edge function also updates the DB, but re-fetching can race with the write
      if (data.tier && profile.value && data.tier !== profile.value.subscription_tier) {
        console.log('Tier mismatch detected, updating local profile to:', data.tier)
        profile.value = {
          ...profile.value,
          subscription_tier: data.tier,
          subscription_status: data.status || profile.value.subscription_status,
        }
        console.log('Profile updated, tier now:', profile.value.subscription_tier)
      }
    } catch (e) {
      console.warn('syncSubscriptionTier failed:', e)
    }
  }

  // Helpers
  const getDefaultPreferences = (): UserPreferences => ({
    theme: 'dark',
    notifications_enabled: true,
    email_notifications: true,
    currency: 'USD',
    units: 'metric',
    language: 'en',
    ai_include_inventory: true,
  })

  return {
    // State
    user,
    session,
    profile,
    loading,
    error,
    isDemoMode,
    // Getters
    isAuthenticated,
    userId,
    userEmail,
    displayName,
    avatarUrl,
    subscriptionTier,
    isAdmin,
    storageUsedBytes,
    storageExtraGb,
    preferences,
    // Actions
    initialize,
    fetchProfile,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithGithub,
    linkDiscord,
    discordStatus,
    unlinkDiscord,
    unlinkIdentity,
    getUserIdentities,
    signOut,
    updateProfile,
    updatePreferences,
    resetPassword,
    changePassword,
    ensureFreshSession
  }
})
